import React, { useState, useEffect, useCallback } from 'react';
import {
    Search,
    Filter,
    FileText,
    Users,
    ChevronLeft,
    ChevronRight,
    X, // Added
    Mail, // Added
    Phone, // Added
    Clock // Added
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { usePermissions } from '../../hooks/usePermissions';
import './ContactAdminPanel.css'; // Assuming you have a CSS file for styles

const ContactAdminPanel = () => {
    const permissions = usePermissions('Contact Forms');
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        role: '',
        startDate: '',
        endDate: '',
        isContacted: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0
    });
    const [stats, setStats] = useState({
        total: 0,
        contacted: 0,
        not_contacted: 0
    });
    const [showFilters, setShowFilters] = useState(false);

    const { loading, error, makeRequest, clearError, handleFetchResponse } = useAuthenticatedApi();

    const fetchContacts = useCallback(async () => {
        clearError();
        const queryParams = new URLSearchParams();

        if (filters.search) queryParams.append('search', filters.search);
        if (filters.role) queryParams.append('role', filters.role);
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.isContacted) queryParams.append('isContacted', filters.isContacted);
        queryParams.append('page', pagination.currentPage);
        queryParams.append('limit', 10);

        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/website-main/contact-us?${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            await handleFetchResponse(fetchResponse);
            const data = await fetchResponse.json();
            if (!data.success) {
                throw new Error('Failed to fetch contacts');
            }
            return data;
        });

        if (response) {
            setContacts(response.data.contacts);
            setPagination(response.data.pagination);
            setStats(response.data.stats);
        }
    }, [filters, pagination.currentPage, makeRequest, clearError]);

    // Fetch contacts from API
    useEffect(() => {
        fetchContacts();
    }, [filters, pagination.currentPage, fetchContacts]);

    const clearFilters = () => {
        setFilters({
            search: '',
            role: '',
            startDate: '',
            endDate: '',
            isContacted: ''
        });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleMarkContacted = async (contactId) => {
        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/website-main/contact-us/${contactId}/mark-contacted`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                }
            });
            await handleFetchResponse(fetchResponse);
            const data = await fetchResponse.json();
            if (!data.success) {
                throw new Error('Failed to mark as contacted');
            }
            return data;
        });

        if (response) {
            setContacts(prev =>
                prev.map(contact =>
                    contact._id === contactId
                        ? { ...contact, isContacted: true }
                        : contact
                )
            );

            // **MODIFIED: Update selected contact if it's the one being viewed**
            if (selectedContact && selectedContact._id === contactId) {
                setSelectedContact(prev => ({ ...prev, isContacted: true }));
            }

            // Update stats
            setStats(prev => ({
                ...prev,
                contacted: prev.contacted + 1,
                not_contacted: prev.not_contacted - 1
            }));
        }
    };

    const handleAddNote = async (contactId, note) => {
        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/website-main/contact-us/${contactId}/notes`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ note }),
            });
            await handleFetchResponse(fetchResponse);
            const data = await fetchResponse.json();
            if (!data.success) {
                throw new Error('Failed to update note');
            }
            return data;
        });

        if (response) {
            setContacts(prev =>
                prev.map(contact =>
                    contact._id === contactId
                        ? { ...contact, note }
                        : contact
                )
            );

            if (selectedContact && selectedContact._id === contactId) {
                setSelectedContact(prev => ({ ...prev, note }));
            }
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'User': return 'bg-blue-100 text-blue-800';
            case 'Individual': return 'bg-green-100 text-green-800';
            case 'ShopOwner': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    return (
        <div className="contact-admin-panel">
            <div className="contact-admin-header">
                <h1>Contact Management</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="filter-toggle"
                >
                    <Filter size={20} />
                    Filters
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
            )}

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon total">
                        <Users size={24} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-number">{stats.total}</div>
                        <div className="stat-label">Total Contacts</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon contacted">
                        <FileText size={24} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-number">{stats.contacted}</div>
                        <div className="stat-label">Contacted</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon not-contacted">
                        <FileText size={24} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-number">{stats.not_contacted}</div>
                        <div className="stat-label">Not Contacted</div>
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="filters-panel">
                    <div className="filters-header">
                        <h3>Filters</h3>
                        <button onClick={clearFilters} className="clear-filters-btn">
                            Clear All
                        </button>
                    </div>

                    <div className="search-box">
                        <Search size={20} />
                        <input
                            type="text"
                            placeholder="Search by name, email, or phone..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>

                    <div className="filter-row">
                        <select
                            value={filters.role}
                            onChange={(e) => handleFilterChange('role', e.target.value)}
                        >
                            <option value="">All Roles</option>
                            <option value="User">User</option>
                            <option value="Individual">Individual</option>
                            <option value="ShopOwner">Shop Owner</option>
                        </select>

                        <select
                            value={filters.isContacted}
                            onChange={(e) => handleFilterChange('isContacted', e.target.value)}
                        >
                            <option value="">All Contacts</option>
                            <option value="true">Contacted</option>
                            <option value="false">Not Contacted</option>
                        </select>

                        <div className="date-filter">
                            <label>From Date:</label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            />
                        </div>

                        <div className="date-filter">
                            <label>To Date:</label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading contacts...</p>
                </div>
            ) : (
                <>
                    <div className="contacts-list">
                        {contacts.map((contact, index) => (
                            <div
                                key={contact._id}
                                className="contact-card-horizontal"
                                // **MODIFIED: Added onClick to open modal**
                                onClick={() => setSelectedContact(contact)}
                            >
                                <div className="contact-main-info">
                                    <div className="contact-header-row">
                                        <div className="contact-basic">
                                            <h3>{contact.name}</h3>
                                            <span className={`role-badge ${getRoleColor(contact.role)}`}>
                                                {contact.role}
                                            </span>
                                            {contact.isContacted && (
                                                <span className="contacted-badge">✓ Contacted</span>
                                            )}
                                        </div>
                                        <div className="contact-date">
                                            {formatDate(contact.createdAt)}
                                        </div>
                                    </div>

                                    <div className="contact-meta-compact">
                                        <span className="contact-email">{contact.email}</span>
                                        <span className="contact-phone">{contact.phone_number}</span>
                                        {contact.attachment_url && (
                                            <a
                                                href={contact.attachment_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="attachment-link"
                                                // **MODIFIED: Stop propagation so link click doesn't open modal**
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <FileText size={14} /> Attachment
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="contact-message-compact">
                                    <strong>Message:</strong>
                                    <span>{contact.message.length > 150 ? contact.message.substring(0, 150) + '...' : contact.message}</span>
                                </div>

                                <div className="contact-notes-section">
                                    <div className="notes-display">
                                        <strong>Notes:</strong>
                                        <span>{contact.note || 'No notes added'}</span>
                                    </div>
                                    <div
                                        className="notes-input-row"
                                        // **MODIFIED: Stop propagation so clicks in this area don't open modal**
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <input
                                            type="text"
                                            placeholder="Add note..."
                                            id={`note-input-${contact._id}`}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddNote(contact._id, e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                            className="quick-note-input"
                                        />
                                        <button
                                            onClick={() => {
                                                const input = document.getElementById(`note-input-${contact._id}`);
                                                if (input.value.trim()) {
                                                    handleAddNote(contact._id, input.value);
                                                    input.value = '';
                                                }
                                            }}
                                            className="add-note-btn"
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>

                                <div
                                    className="contact-actions-compact"
                                    // **MODIFIED: Stop propagation so button click doesn't open modal**
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        onClick={() => handleMarkContacted(contact._id)}
                                        disabled={contact.isContacted}
                                        className={`contact-btn ${contact.isContacted ? 'contacted' : 'mark-contacted'}`}
                                    >
                                        {contact.isContacted ? 'Contacted' : 'Mark Contacted'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="pagination">
                        <button
                            className="pagination-btn"
                            disabled={!pagination.hasPrevPage}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                        >
                            <ChevronLeft size={16} />
                            Previous
                        </button>

                        <span className="pagination-info">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>

                        <button
                            className="pagination-btn"
                            disabled={!pagination.hasNextPage}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </>
            )}

            {/* **MODIFIED: Added Modal Rendering** */}
            {selectedContact && (
                <ContactDetailModal
                    contact={selectedContact}
                    onClose={() => setSelectedContact(null)}
                    formatDate={formatDate}
                    getRoleColor={getRoleColor}
                    onMarkContacted={handleMarkContacted}
                    onAddNote={handleAddNote}
                />
            )}
        </div>
    );
};

// **NEW COMPONENT: Contact Detail Modal**
const ContactDetailModal = ({ contact, onClose, formatDate, getRoleColor, onMarkContacted, onAddNote }) => {
    // Separate state for the modal's note input
    const [note, setNote] = useState(contact.note || '');

    // Update internal note state if contact prop changes (e.g., after quick add)
    useEffect(() => {
        setNote(contact.note || '');
    }, [contact.note]);

    const handleNoteSave = () => {
        onAddNote(contact._id, note);
    };

    // Close modal if overlay is clicked, but not content
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-content">
                <div className="modal-header">
                    <div className="modal-header-info">
                        <h2>{contact.name}</h2>
                        <span className={`role-badge ${getRoleColor(contact.role)}`}>
                            {contact.role}
                        </span>
                        {contact.isContacted && (
                            <span className="contacted-badge">✓ Contacted</span>
                        )}
                    </div>
                    <button onClick={onClose} className="modal-close-btn">
                        <X size={24} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="modal-contact-meta">
                        <div className="meta-item">
                            <Mail size={16} /> {contact.email}
                        </div>
                        <div className="meta-item">
                            <Phone size={16} /> {contact.phone_number}
                        </div>
                        <div className="meta-item">
                            <Clock size={16} /> Submitted: {formatDate(contact.createdAt)}
                        </div>
                        {contact.attachment_url && (
                            <div className="meta-item">
                                <FileText size={16} />
                                <a href={contact.attachment_url} target="_blank" rel="noopener noreferrer" className="attachment-link-modal">
                                    View Attachment ({contact.attachment_filename || 'View File'})
                                </a>
                            </div>
                        )}
                    </div>

                    <div className="modal-message-section">
                        <h3>Message</h3>
                        {/* The 'message-content-full' class uses white-space: pre-wrap to preserve newlines */}
                        <div className="message-content-full">
                            {contact.message}
                        </div>
                    </div>

                    <div className="modal-notes-section">
                        <h3>Admin Notes</h3>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add notes here..."
                            className="modal-note-textarea"
                        />
                        <button onClick={handleNoteSave} className="save-note-btn">
                            Save Note
                        </button>
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        onClick={() => onMarkContacted(contact._id)}
                        disabled={contact.isContacted}
                        className={`contact-btn ${contact.isContacted ? 'contacted' : 'mark-contacted'}`}
                    >
                        {contact.isContacted ? 'Contacted' : 'Mark as Contacted'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactAdminPanel;